import type { FacetDefinition } from "./types";

export type ParsedArtPackCaption = {
  source: string;
  title: string | null;
  facets: Record<string, string[]>;
  caption: string;
  structured: boolean;
};

const slug = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const decodeEntities = (value: string) => value
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">")
  .replace(/&quot;/g, '"')
  .replace(/&apos;/g, "'")
  .replace(/&amp;/g, "&");

function singleTag(source: string, name: string): string | null {
  const openings = source.match(new RegExp(`<${name}>`, "gi")) ?? [];
  const closings = source.match(new RegExp(`</${name}>`, "gi")) ?? [];
  if (openings.length !== closings.length) throw new Error(`Malformed <${name}> section.`);
  if (openings.length > 1) throw new Error(`Only one <${name}> section is allowed.`);
  if (!openings.length) return null;
  const match = source.match(new RegExp(`<${name}>([\\s\\S]*?)</${name}>`, "i"));
  if (!match) throw new Error(`Malformed <${name}> section.`);
  return decodeEntities(match[1].trim());
}

function parseAspects(source: string, definitions: FacetDefinition[]): Record<string, string[]> {
  const facets: Record<string, string[]> = {};
  const byName = new Map<string, FacetDefinition>();
  for (const definition of definitions) {
    byName.set(definition.key.toLowerCase(), definition);
    byName.set(slug(definition.label), definition);
  }
  const entries = source.split(/\r?\n|;/).map((entry) => entry.trim()).filter(Boolean);
  for (const entry of entries) {
    const separator = entry.indexOf(":");
    if (separator < 1) throw new Error(`Invalid aspect “${entry}”. Use facet: value, value.`);
    const rawName = entry.slice(0, separator).trim();
    const definition = byName.get(rawName.toLowerCase()) ?? byName.get(slug(rawName));
    if (!definition) throw new Error(`Unknown facet “${rawName}”. Create it in the Facets tab first.`);
    const allowed = new Set(definition.suggestedValues);
    const values = entry.slice(separator + 1).split(",").map(slug).filter(Boolean);
    if (!values.length) throw new Error(`Facet “${definition.label}” needs at least one value.`);
    for (const value of values) {
      if (!allowed.has(value)) throw new Error(`Unknown value “${value}” for ${definition.label}. Add it in the Facets tab first.`);
    }
    facets[definition.key] = [...new Set([...(facets[definition.key] ?? []), ...values])];
  }
  return facets;
}

export function parseArtPackCaption(source: string, definitions: FacetDefinition[]): ParsedArtPackCaption {
  const normalized = source.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  const title = singleTag(normalized, "title");
  const aspects = singleTag(normalized, "aspects");
  const captionSection = singleTag(normalized, "caption");
  const structured = title !== null || aspects !== null || captionSection !== null;
  if (title !== null && !title.trim()) throw new Error("<title> cannot be empty.");

  let caption = captionSection;
  if (structured && caption === null) {
    caption = normalized
      .replace(/<title>[\s\S]*?<\/title>/gi, "")
      .replace(/<aspects>[\s\S]*?<\/aspects>/gi, "")
      .trim();
  }

  return {
    source: normalized,
    title: title?.trim() || null,
    facets: aspects === null ? {} : parseAspects(aspects, definitions),
    caption: (caption ?? normalized).trim(),
    structured,
  };
}
